// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, ShipmentPackageDto, ShipmentPackageLineDto } from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { IconPresentationProps, IconProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip/Tooltip";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { StatusProps } from "@store/Library/Components/Status";
import { LoaderProps } from "@store/Library/Components/Toolkit/Loader";
import ShipmentsRow from "@store/Library/Widgets/OrderDetails/ShipmentsRow";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Props";

import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import ArrowiOSDownward from "@store/Library/Components/Toolkit/Icons/ArrowiOSDownward";
import ArrowiOSUpward from "@store/Library/Components/Toolkit/Icons/ArrowiOSUpward";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useContext, useState } from "react";
import { css } from "styled-components";

export interface Props {
    orderLineId: string;
    showDeliveryStatus: boolean;
    showInvoiceStatus?: boolean;
    openInvoicesInNewTab?: boolean;
}

export type Shipment = ShipmentPackageLineDto & Partial<ShipmentPackageDto>;

export interface OrderDetailsShipmentPackagesStyles {
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    shipmentDateCell?: DataTableCellProps;
    carrierCell?: DataTableCellProps;
    shipViaCell?: DataTableCellProps;
    trackingNumberCell?: DataTableCellProps;
    tableWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    actionButton?: ButtonPresentationProps;
    statusStyles?: StatusProps;
    tooltipStyles?: TooltipPresentationProps;
    iconStyles?: IconPresentationProps;
    loadingWrapper?: InjectableCss;
    loaderStyle?: LoaderProps;
    downloadButton?: ButtonPresentationProps;
    buttonIcon?: IconProps;
    buttonLoader?: LoaderProps;
}

export const shipmentPackagesStyles: OrderDetailsShipmentPackagesStyles = {
    label: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};

            button {
                color: ${x => x.theme.colors.text.link};
                background: none;
                border: none;
                padding: 0;
                font-size: 15px;
                cursor: pointer;
            }
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
        `,
    },
    tableWrapper: {
        css: css`
            position: relative;
            width: 100%;
            overflow-x: auto;
            ${breakpointValues([null, null, null, "auto", "visible"], "overflow-x", "up")};
            ${breakpointValues([pxToRem(24, 74, 0, 16), null, pxToRem(16, 74, 0, 16), null, null], "padding", "up")};
            margin-top: 1em;
        `,
    },
    actionButton: {
        sizeVariant: "small",
        variant: "tertiary",
    },
    statusStyles: {
        extendedStyles: {
            wrapper: {
                css: css`
                    justify-content: center;
                `,
            },
            content: {
                css: css`
                    ${({ theme }) => `
                        font-weight: ${theme.design.typography.weight.bold};
                        font-size: ${theme.design.typography.size.tiny};
                    `}
                `,
            },
        },
    },
    tooltipStyles: {
        cssOverrides: {
            tooltipContainer: css`
                white-space: normal;
            `,
        },
        mergeCss: true,
        typographyProps: {
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
            `,
        },
        iconProps: {
            src: AlertCircle,
            color: "text.link",
        },
    },

    loadingWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },

    downloadButton: {
        variant: "tertiary",
    },
    buttonIcon: {
        color: "link",
        size: 22,
        src: Download,
        css: css`
            max-width: ${pxToRem(20)};
            margin-right: ${pxToRem(8)};
        `,
    },
    buttonLoader: {
        height: 0,
        size: 20,
        extendedStyles: {
            wrapper: {
                css: css`
                    max-width: ${pxToRem(20)};
                    margin-right: ${pxToRem(10)};
                `,
            },
        },
    },
};

const styles = shipmentPackagesStyles;

const findShipmentsForEachOrderLine = (
    currentOrderLineId: string,
    shipmentPackages: ShipmentPackageDto[],
): Shipment[] => {
    const shipmentsExtracted: Shipment[] = [];

    shipmentPackages?.forEach(shipment => {
        if (!shipment.shipmentPackageLineDtos) {
            return;
        }

        const { shipmentDate } = shipment;

        const shipmentLine = shipment.shipmentPackageLineDtos.find(p => p.orderHistoryLineId === currentOrderLineId);
        if (shipmentLine) {
            shipmentsExtracted.push({ ...shipmentLine, shipmentDate });
        }
    });

    return shipmentsExtracted;
};

const OrderDetailsShipment = ({
    orderLineId,
    // Greif
    showDeliveryStatus,
    showInvoiceStatus,
    openInvoicesInNewTab,
}: Props) => {
    const [showShipments, setShowShipments] = useState(true); // Greif
    const ShipmentClickHandler = () => {
        setShowShipments(!showShipments);
    };

    const { value: order } = useContext(OrderStateContext);
    const {
        properties: { shipments: packagesInfo },
        shipmentPackages,
    } = order as OrderModel;

    if (!shipmentPackages) {
        return null;
    }

    const shipmentsDisplay = findShipmentsForEachOrderLine(orderLineId, shipmentPackages);

    if (!shipmentsDisplay || shipmentsDisplay.length === 0) {
        return null;
    }

    const heading = showShipments ? "Hide Shipments" : shipmentsDisplay.length === 0 ? "" : "Show Shipments";
    const linkIcon = linkGetActionLinkProps(showShipments ? ArrowiOSUpward : ArrowiOSDownward, "left");

    return (
        <>
            <StyledWrapper {...styles.linkWrapper}>
                <Link {...linkIcon} aria-expanded={showShipments} onClick={ShipmentClickHandler}>
                    {translate(heading)}
                </Link>
            </StyledWrapper>
            {showShipments && (
                <StyledWrapper {...styles.tableWrapper}>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.shipmentDateHeader}>
                                {translate("Shipping Document")}
                            </DataTableHeader>
                            <DataTableHeader {...styles.carrierHeader}>{translate("Ship Date")}</DataTableHeader>
                            <DataTableHeader {...styles.shipViaHeader}>{translate("QTY Shipped")}</DataTableHeader>
                            <DataTableHeader {...styles.shipViaHeader}>{translate("Price")}</DataTableHeader>
                            <DataTableHeader {...styles.trackingNumberHeader}>
                                {translate("Delivery Date")}
                            </DataTableHeader>
                            <DataTableHeader {...styles.shipViaHeader}>{translate("Invoice Document")}</DataTableHeader>
                            {showDeliveryStatus && (
                                <DataTableHeader {...styles.trackingNumberHeader}>
                                    {translate("Delivery Status")}
                                </DataTableHeader>
                            )}
                            {showInvoiceStatus && (
                                <DataTableHeader {...styles.shipViaHeader}>{translate("Invoice(s)")}</DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {shipmentsDisplay.map(shipment => (
                                <ShipmentsRow
                                    key={shipment.id}
                                    shipment={shipment}
                                    styles={styles}
                                    showDeliveryStatus={showDeliveryStatus}
                                    showInvoiceStatus={showInvoiceStatus}
                                    openInvoicesInNewTab={openInvoicesInNewTab}
                                />
                            ))}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            )}
        </>
    );
};

export default OrderDetailsShipment;
